import { useState } from 'react';
import { LIMITS, sanitizeByKind, validateByKind } from './fields';
import './ValidatedInput.css';

/**
 * Controlled input that enforces project validation rules while typing.
 * kind: text | textarea | number | email | phone | password | otp
 */
export default function ValidatedInput({
  kind = 'text',
  value = '',
  onChange,
  name,
  required = false,
  label,
  className,
  rows = 4,
  ...rest
}) {
  const [showPassword, setShowPassword] = useState(false);
  const max =
    kind === 'textarea'
      ? LIMITS.textarea
      : kind === 'password'
        ? LIMITS.passwordMax
        : kind === 'phone'
          ? LIMITS.phone
          : kind === 'otp'
            ? LIMITS.otp
            : LIMITS.text;

  const handleChange = (e) => {
    const next = sanitizeByKind(kind, e.target.value);
    if (onChange) {
      if (typeof onChange === 'function') {
        const synthetic = {
          ...e,
          target: { ...e.target, name: name || e.target.name, value: next },
        };
        onChange(synthetic, next);
      }
    }
  };

  const common = {
    name,
    value: value ?? '',
    onChange: handleChange,
    required,
    maxLength: max,
    className,
    'data-kind': kind,
    ...rest,
  };

  if (kind === 'email') {
    common.type = 'email';
    common.autoCapitalize = 'none';
    common.autoCorrect = 'off';
    common.spellCheck = false;
    common.inputMode = 'email';
  } else if (kind === 'phone') {
    common.type = 'tel';
    common.inputMode = 'numeric';
    common.pattern = '\\d{10}';
  } else if (kind === 'number') {
    common.type = 'text';
    common.inputMode = 'decimal';
  } else if (kind === 'password') {
    common.type = showPassword ? 'text' : 'password';
  } else if (kind === 'otp') {
    common.type = 'text';
    common.inputMode = 'numeric';
    common.autoComplete = 'one-time-code';
  }

  if (kind === 'textarea') {
    return <textarea rows={rows} {...common} />;
  }

  if (kind === 'password') {
    return (
      <span className="validated-password">
        <input {...common} />
        <button
          type="button"
          className="validated-password__toggle"
          onClick={() => setShowPassword((v) => !v)}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {showPassword ? (
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path
                fill="currentColor"
                d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"
              />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
              <path
                fill="currentColor"
                d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"
              />
            </svg>
          )}
        </button>
      </span>
    );
  }

  return <input {...common} />;
}

export function validateFields(fields) {
  const errors = [];
  for (const f of fields) {
    const err = validateByKind(f.kind || 'text', f.value, {
      required: f.required,
      label: f.label,
    });
    if (err) errors.push(err);
  }
  return errors;
}
